import { h } from "../core/dom.js";
import { icon } from "../lib/icons.js";
import { navigate } from "../core/router.js";
import { toDisplayWeight, weightLabel, fmtTimeShort, fmtDistance, fmtPace } from "../lib/format.js";

// One-line value of a logged set, formatted for its exercise's metric type. Shared by the
// live logger's "last time" line and the read-only history view so they never drift.
export function setSummaryText(metricType, set, { rpe = false } = {}) {
  const base = (() => {
    switch (metricType) {
      case "duration":
        return fmtTimeShort(set.duration_seconds) || "—";
      case "distance_duration": {
        const parts = [];
        if (set.distance_m != null) parts.push(fmtDistance(set.distance_m));
        if (set.duration_seconds != null) parts.push(fmtTimeShort(set.duration_seconds));
        const pace = fmtPace(set.duration_seconds, set.distance_m);
        return (parts.join(" · ") || "—") + (pace ? ` (${pace})` : "");
      }
      case "bodyweight_reps":
        return set.reps != null ? `${set.reps} reps` : "—";
      default:
        return `${toDisplayWeight(set.weight_kg)}${weightLabel()}×${set.reps ?? "—"}`;
    }
  })();
  // RPE is an outcome, not a target — only surfaced as reference (opt-in), never prefilled.
  return rpe && set.rpe != null ? `${base} @${set.rpe}` : base;
}

export function topbar(title, { eyebrow, sub, icon: iconProp, badge, actions, back } = {}) {
  const iconNode = iconProp
    ? (typeof iconProp === "string" ? h("span", { class: "topbar-icon" }, icon(iconProp, { size: 20 })) : iconProp)
    : null;
  const badgeNode = badge
    ? (typeof badge === "string" ? h("span", { class: "badge topbar-badge" }, badge) : badge)
    : null;
  const titleNode = typeof title === "string" ? h("h1", { tabindex: "-1" }, title) : title;
  const titleRow = h("div", { class: "topbar-title-row" }, iconNode, titleNode, badgeNode);

  const content = h("div", { class: "topbar-content" },
    eyebrow ? h("div", { class: "topbar-eyebrow eyebrow" }, eyebrow) : null,
    titleRow,
    sub ? h("div", { class: "sub topbar-sub" }, sub) : null
  );

  return h(
    "header",
    { class: "topbar" },
    h("div", { class: "topbar-main" },
      back ? (typeof back === "string" ? backLink("Back", back) : (back === true ? backLink() : back)) : null,
      content
    ),
    actions && h("div", { class: "topbar-actions" }, ...[].concat(actions))
  );
}

export function backLink(label = "Back", to) {
  return h(
    "button",
    { class: "back-link", onClick: () => (to ? navigate(to) : history.back()) },
    icon("back", { size: 18 }),
    label
  );
}

// An exercise image entry is either a local seeded path (served from /exercises/) or a
// full external https URL (user-created exercises paste an allowlisted link). Resolve both.
export function exerciseImgSrc(img) {
  return /^https?:\/\//i.test(img) ? img : `/exercises/${img}`;
}

export function exerciseThumb(images, { size } = {}) {
  const src = Array.isArray(images) && images.length ? exerciseImgSrc(images[0]) : null;
  if (src) {
    return h("img", { class: "ex-thumb", src, alt: "", loading: "lazy", style: size ? { width: size + "px", height: size + "px" } : null });
  }
  return h("div", { class: "ex-thumb placeholder" }, icon("dumbbell", { size: 22 }));
}

// Compute superset display labels for an ordered list of exercises. Exercises sharing the
// same non-null `superset_group` form a group; groups with ≥2 members get a letter (A, B, …)
// in first-appearance order, and each member an index within it (A1, A2). Returns an array
// aligned to `items` of { label, letter, index, size } for members, or null for standalone.
export function supersetLabels(items, getGroup = (e) => e.superset_group) {
  const groups = new Map(); // group value -> array of item indices
  items.forEach((it, i) => {
    const g = getGroup(it);
    if (g == null) return;
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g).push(i);
  });
  const out = new Array(items.length).fill(null);
  let letterIdx = 0;
  for (const [, idxs] of groups) {
    if (idxs.length < 2) continue;
    const letter = String.fromCharCode(65 + letterIdx);
    letterIdx++;
    idxs.forEach((itemIdx, k) => {
      out[itemIdx] = { letter, index: k + 1, size: idxs.length, label: `${letter}${k + 1}` };
    });
  }
  return out;
}

export function muscleBadges(primaryIds = [], secondaryIds = [], muscleNameFn) {
  const badges = [];
  for (const id of primaryIds) {
    const name = muscleNameFn(id);
    if (name) badges.push(h("span", { class: "badge badge-muscle" }, name));
  }
  for (const id of secondaryIds) {
    const name = muscleNameFn(id);
    if (name) badges.push(h("span", { class: "badge badge-muscle secondary" }, name));
  }
  return h("div", { class: "chip-row" }, ...badges);
}
